import { useMemo } from 'react';
import { Check, Description } from '@mui/icons-material';
import { BarChartOutlined, PercentageOutlined, SwapOutlined, TagOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LabelList, LineChart, Line } from 'recharts';
import { Divider } from 'antd';
import { Navigate, useNavigate } from 'react-router-dom';
import { Handshake } from 'assets/svg';

type MetricPillTone = 'positive' | 'negative';

interface MetricData {
    title: string;
    subtitle: string;
}

interface ChartDataItem {
    name: string;
    BaseScenario: number;
    FinalScenario: number;
}

interface EnrichedMetricData extends MetricData {
    percentage: string;
    tone: MetricPillTone;
}

const metrics: MetricData[] = [
    { title: 'Net Sales', subtitle: '(€M)' },
    { title: 'Delivery Margin', subtitle: '(€M)' },
    { title: 'Trade Margin', subtitle: '(€M)' },
    { title: 'Volume', subtitle: '(€M)' },
];

const COLORS = {
    baseScenario: 'hsl(217, 91%, 60%)', // Blue color for base scenario
    finalScenario: 'hsl(160, 51%, 49%)', // Teal/green color for final scenario
    // Badge colors
    positiveBackground: 'hsl(145, 45%, 90%)',
    positiveText: 'hsl(145, 65%, 35%)',
    negativeBackground: 'hsl(0, 45%, 94%)',
    negativeText: 'hsl(0, 70%, 50%)',
};

function MetricPill({ value, tone }: { value: string; tone: MetricPillTone }) {
    const isPositive = tone === 'positive';
    return (
        <span
            className="inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={{
                background: isPositive ? COLORS.positiveBackground : COLORS.negativeBackground,
                color: isPositive ? COLORS.positiveText : COLORS.negativeText,
            }}
        >
            {value}
        </span>
    );
}

const rawData: ChartDataItem[] = [
    { name: 'Net Sales', BaseScenario: 180, FinalScenario: 189 },
    { name: 'Delivery Margin', BaseScenario: 200, FinalScenario: 196 },
    { name: 'Trade Margin', BaseScenario: 150, FinalScenario: 155 },
    { name: 'Volume', BaseScenario: 275, FinalScenario: 286 },
];

// Calculate percentage difference and determine tone
function calculateMetricPercentage(base: number, projected: number): { percentage: string; tone: MetricPillTone } {
    const diff = projected - base;
    const percentChange = (diff / base) * 100;
    const sign = percentChange >= 0 ? '+' : '';
    const formattedPercent = `${sign}${Math.round(percentChange)}%`;
    const tone: MetricPillTone = percentChange >= 0 ? 'positive' : 'negative';
    return { percentage: formattedPercent, tone };
}

// Enrich metrics with calculated percentages
function enrichMetricsWithData(metrics: MetricData[], chartData: ChartDataItem[]): EnrichedMetricData[] {
    return metrics.map((metric, index) => {
        const dataPoint = chartData[index];
        const { percentage, tone } = calculateMetricPercentage(dataPoint.BaseScenario, dataPoint.FinalScenario);
        return { ...metric, percentage, tone };
    });
}

// Calculate dynamic Y-axis configuration based on data

function calculateYAxisConfig(maxTotal: number): { maxValue: number; ticks: number[] } {
    const paddedMax = maxTotal * 1.15;

    let interval: number;
    if (paddedMax <= 100) {
        interval = 25;
    } else if (paddedMax <= 500) {
        interval = 50;
    } else if (paddedMax <= 1000) {
        interval = 100;
    } else {
        interval = Math.pow(10, Math.floor(Math.log10(paddedMax)) - 1) * 5;
    }

    const maxValue = Math.ceil(paddedMax / interval) * interval;

    // Generate tick values (4 ticks including 0)
    const tickCount = 4;
    const tickInterval = maxValue / tickCount;
    const ticks = Array.from({ length: tickCount + 1 }, (_, i) => Math.round(i * tickInterval));

    return { maxValue, ticks };
}

function BarLabel(props: any) {
    const { x, y, width, value } = props;
    return (
        <text x={x + width / 2} y={y - 8} textAnchor="middle" fill="#374151" fontSize={12} fontWeight={500}>
            {value}
        </text>
    );
}

function CustomLegend() {
    return (
        <div className="flex items-center justify-center gap-5 mt-4 pt-3">
            <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ background: COLORS.baseScenario }} />
                <span className="text-xs text-gray-600">Base</span>
            </div>
            <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ background: COLORS.finalScenario }} />
                <span className="text-xs text-gray-600">Projected</span>
            </div>
        </div>
    );
}

export default function PerformanceSummaryCard() {
    // Use raw data directly for grouped bars
    const chartData = useMemo(() => {
        return rawData;
    }, []);

    // Enrich metrics with calculated percentages
    const enrichedMetrics = useMemo(() => {
        return enrichMetricsWithData(metrics, chartData);
    }, [chartData]);

    // Calculate dynamic Y-axis configuration based on data
    const { maxValue, ticks } = useMemo(() => {
        const maxValue = Math.max(...chartData.flatMap((item) => [item.BaseScenario, item.FinalScenario]));
        return calculateYAxisConfig(maxValue);
    }, [chartData]);

    const navigate = useNavigate();

    function handleOpenInNewWindow() {
        navigate('executive-summary');
    }

    // Trend data for mini charts with more curve variation
    const upwardTrend = [
        { value: 8 },
        { value: 12 },
        { value: 10 },
        { value: 18 },
        { value: 16 },
        { value: 22 },
        { value: 20 },
        { value: 28 },
    ];
    const downwardTrend = [
        { value: 28 },
        { value: 25 },
        { value: 27 },
        { value: 22 },
        { value: 20 },
        { value: 18 },
        { value: 15 },
        { value: 12 },
    ];
    const slightUpwardTrend = [
        { value: 18 },
        { value: 20 },
        { value: 19 },
        { value: 22 },
        { value: 21 },
        { value: 23 },
        { value: 22 },
        { value: 24 },
    ];

    const SummaryCard = ({
        title,
        icon: Icon,
        iconBgColor,
        iconColor,
        cardBgColor,
        primaryMetric,
        primaryLabel,
        secondaryMetric,
        secondaryLabel,
        comparison,
        comparisonValue,
        isPositive,
        trendData,
    }: {
        title: string;
        icon: React.ElementType;
        iconBgColor: string;
        iconColor: string;
        cardBgColor: string;
        primaryMetric: string;
        primaryLabel: string;
        secondaryMetric: string;
        secondaryLabel: string;
        comparison: string;
        comparisonValue: string;
        isPositive: boolean;
        trendData: { value: number }[];
    }) => (
        <div
            className="rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
            style={{ backgroundColor: cardBgColor }}
        >
            <div className="flex items-center gap-3 mb-4">
                <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: iconBgColor }}
                >
                    <Icon style={{ fontSize: 20, color: iconColor }} />
                </div>
                <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            </div>

            <div className="mb-2">
                <p className="text-xs text-gray-500 mb-1">{primaryLabel}</p>
                <div className="flex items-center justify-between">
                    <p className="text-2xl font-bold text-gray-900">{primaryMetric}</p>
                    {/* <div className="w-20 h-10">
                        <LineChart
                            width={80}
                            height={40}
                            data={trendData}
                            margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
                        >
                            <Line
                                type="basis"
                                dataKey="value"
                                stroke="#9CA3AF"
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                            />
                        </LineChart>
                    </div> */}
                </div>
            </div>

            <div className="mb-2">
                <p className="text-xs text-gray-500">{secondaryLabel}</p>
                <div className="flex items-center gap-2">
                    {/* <span className="text-sm font-semibold text-gray-900">{secondaryMetric}</span> */}
                    <span className="text-xs text-gray-500">{comparison}</span>
                    <span
                        className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}
                        style={isPositive ? { color: '#10B981' } : {}}
                    >
                        {isPositive ? '↑' : '↓'} {comparisonValue}
                    </span>
                </div>
            </div>

            {/* <div className="space-y-2 pt-4 border-t border-gray-100">
                {insights.map((insight, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <Check style={{ fontSize: 16, color: '#4b5563' }} />
                        <span className="text-xs text-gray-600">{insight}</span>
                    </div>
                ))}
            </div> */}
        </div>
    );

    return (
        <div className="">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-[4px] rounded-full bg-violet-600" />
                        <Description style={{ fontSize: 20, color: '#7C3AED' }} />
                        <h2 className="text-lg mb-0 font-semibold text-gray-900">Executive Summary</h2>
                    </div>
                    <p
                        onClick={handleOpenInNewWindow}
                        className="text-sm cursor-pointer text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        View more <span className="ml-1">→</span>
                    </p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <SummaryCard
                        title="Pricing"
                        icon={TagOutlined}
                        iconBgColor="hsl(45 95% 55%/ 0.12)"
                        iconColor="hsl(45 95% 55%)"
                        cardBgColor="#F9FAFB"
                        primaryMetric="105"
                        primaryLabel="Quality Growth Index"
                        secondaryMetric="QGI 1.06"
                        secondaryLabel=""
                        comparison="vs 108"
                        comparisonValue="-2.8%"
                        isPositive={false}
                        trendData={upwardTrend}
                        // insights={[
                        //     'Quality Growth Index (QGI)',
                        //     'Price/Lbs Change Contribution',
                        //     'Competitive Price Landscape',
                        // ]}
                    />
                    <SummaryCard
                        title="Trade Promo"
                        icon={PercentageOutlined}
                        iconBgColor="hsl(205 65% 52%/ 0.12)"
                        iconColor="hsl(205 65% 52%)"
                        cardBgColor="#F9FAFB"
                        primaryMetric="0.7"
                        primaryLabel="Promo ROI"
                        secondaryMetric="vs YA 1.61"
                        secondaryLabel=""
                        comparison="vs 0.58"
                        comparisonValue="+0.12"
                        isPositive={true}
                        trendData={downwardTrend}
                        // insights={['ROI by Channel', 'Star Ratings (Promoted GSV)', 'Nutrifuel Profit Pool %']}
                    />
                    <SummaryCard
                        title="Trade Architecture"
                        icon={Handshake}
                        iconBgColor="hsl(330 65% 52%/ 0.12)"
                        iconColor="hsl(330 65% 52%)"
                        cardBgColor="#F9FAFB"
                        primaryMetric="102"
                        primaryLabel="Trade Leverage"
                        secondaryMetric="TLI 100.7"
                        secondaryLabel=""
                        comparison="vs 94"
                        comparisonValue="+8.5%"
                        isPositive={true}
                        trendData={slightUpwardTrend}
                        // insights={[
                        //     '2023 Trade Leverage Index (TLI)',
                        //     '2023 Target Trade Rate % (incl. Adjustments) vs Projection',
                        //     '2024 Total Trade Rate % Earned (vs Target)',
                        // ]}
                    />
                </div>
            </div>
        </div>
    );
}
